/**
 * Vincent B920 + B921 (Eric 2026-02-14 SPEC STRICK PROD) — Boîte
 * repliable multi-source pour la légende des locuteurs.
 *
 * B920 : conteneur repliable via un triangle en haut à droite. L'état
 *        `collapsed` est géré par le parent (VideoDetailPage) — donc
 *        persistant à travers les 3 sous-onglets Whisper / Fireflies /
 *        Fusionnée. La boîte peut rester fermée ou ouverte quand on
 *        change de sous-onglet.
 * B921 : tableau à 4 colonnes montrant TOUS les locuteurs des 3 sources
 *        avec leurs métriques (nb tours + durée min). Les 2 sources
 *        NON-actives sont grisées ; les locuteurs absents de la source
 *        active sont grisés + italique + strikethrough sur leur nom.
 *
 * Note : les couleurs (`speakerColorMap`) sont stables entre les 3
 * sources — un même locuteur a la même couleur qu'il apparaisse dans
 * Whisper, Fireflies ou Fusionnée. C'est essentiel pour que Vincent
 * puisse comparer visuellement les 3 transcriptions sans confusion.
 */
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { worstDivergence, classerEcarts } from "../../lib/speakerDivergence";
import { SpeakerDivergenceModal } from "./SpeakerDivergenceModal";
import { EcartsGroupesModal } from "./EcartsGroupesModal";
import { AppariementLocuteursModal } from "./AppariementLocuteursModal";
import { estGenerique, proposerAppariements } from "../../lib/appariementLocuteurs";
import { API_BASE as API } from "../../lib/apiBase";
import { normalizeSpeakerKey } from "../../lib/speakerNormalize";

const _fmtMin = (seconds, lang) => {
  if (!seconds || seconds <= 0) return "—";
  const mins = seconds / 60;
  return mins.toLocaleString(
    lang === "fr" ? "fr-CA" : "en-CA",
    { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  );
};

const _fmtTurns = (turns, lang) => {
  if (!turns || turns <= 0) return "—";
  const suffix = lang === "fr"
    ? (turns > 1 ? "tours" : "tour")
    : (turns > 1 ? "turns" : "turn");
  return `${turns} ${suffix}`;
};

// Vincent B922 (Eric 2026-02-14 SPEC STRICK PROD) — La logique
// de calcul de divergence est extraite dans `lib/speakerDivergence.js`
// pour rester testable via un simple require() côté Jest sans avoir
// besoin de parser le JSX. Voir tests B922_divergence_highlight.

export const SpeakerLegendBox = ({
  lang,
  activeSubTab, // "whisper" | "fireflies" | "merged"
  speakerColorMap, // { [name]: hex }
  speakerStatsBySource, // { [name]: { whisper: {turns, seconds, words}, fireflies: {...}, merged: {...} } }
  // Tâche 1397 — Quelles sources EXISTENT pour cette vidéo. Sans cette
  // information, une source jamais générée passait pour « 100 % divergente ».
  sourcesPresentes,
  // Tâche 1397 — Seuil d'alerte réglé par l'organisation (défaut 50 %).
  seuilDivergencePct = 50,
  // Tâche 1398 — Les tours par source, pour la comparaison côte à côte.
  segmentsBySource,
  onSeek,
  // Tâche 1397 — Fourni seulement quand la personne peut régler le seuil.
  onSeuilChange,
  speakerFilter,
  toggleSpeakerFilter,
  collapsed,
  setCollapsed,
  // Vincent B937 (Eric 2026-02-14 SPEC STRICK PROD) — Fusion manuelle
  // de locuteurs. Le parent passe l'id de la vidéo + un callback pour
  // rafraîchir les transcriptions après le POST /speakers/merge.
  videoId,
  canEdit = false,
  onSpeakersMerged,
}) => {
  const allNames = Object.keys(speakerStatsBySource || {}).sort((a, b) =>
    String(a).localeCompare(String(b), lang === "fr" ? "fr" : "en", { sensitivity: "base" }),
  );
  // Vincent B937 — Sélection cochée pour fusion (Set<name>).
  const [checkedNames, setCheckedNames] = useState(() => new Set());
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const toggleCheck = (name) => {
    setCheckedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const clearChecks = () => setCheckedNames(new Set());
  // Tâche 1314 (Eric 2026-06-30, 2e passe) — « Fusionner ces N locuteurs »
  // ne faisait RIEN, en silence. Le serveur n'était pas en cause (vérifié en
  // réel : 200, 3 segments renommés). Le nom canonique était demandé par
  // `window.prompt`, que Chrome refuse d'ouvrir dès que la personne a coché
  // « empêcher cette page de créer des dialogues supplémentaires » — et il
  // rend alors `null` SANS erreur : le clic mourait sur le `return` suivant.
  // C'est aussi la règle du projet depuis la tâche 1315 : aucune fenêtre
  // système, le texte doit rester lisible à l'écran.
  const [mergeDialog, setMergeDialog] = useState({ open: false, names: [], value: "" });
  // Tâche 1398 — Quel locuteur, et entre quelles deux sources, on compare.
  const [divergenceOuverte, setDivergenceOuverte] = useState(null);
  // Tâche 1399 — La vue groupée de TOUS les écarts de la vidéo.
  const [ecartsGroupesOuverts, setEcartsGroupesOuverts] = useState(false);
  // Tâche 1406 — Report des noms d'une source vers celle qui n'en a pas.
  const [appariementOuvert, setAppariementOuvert] = useState(false);
  // Tâche 1397 — Valeur en cours de saisie, distincte du seuil appliqué :
  // sans ça, la légende se recalculerait à chaque frappe et clignoterait.
  const [seuilBrouillon, setSeuilBrouillon] = useState(seuilDivergencePct);
  useEffect(() => { setSeuilBrouillon(seuilDivergencePct); }, [seuilDivergencePct]);
  const handleMerge = () => {
    if (checkedNames.size < 2 || !videoId) return;
    const checkedArr = Array.from(checkedNames);
    // Nom canonique proposé = 1er coché (dans l'ordre alphabétique).
    const suggested = checkedArr
      .slice()
      .sort((a, b) => String(a).localeCompare(String(b), lang === "fr" ? "fr" : "en", { sensitivity: "base" }))[0];
    setMergeDialog({ open: true, names: checkedArr, value: suggested });
  };
  const runMerge = async (checkedArr, canonical) => {
    if (!canonical || !canonical.trim()) return;
    setMergeDialog((d) => ({ ...d, open: false }));
    setMergeSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/speakers/merge`,
        { names: checkedArr, canonical: canonical.trim() },
        { withCredentials: true },
      );
      const n = data?.total_segments_renamed ?? 0;
      // Tâche 1314 — zéro segment renommé n'est PAS une réussite : c'est
      // le cas « il ne se passe rien » qu'Eric a signalé. On le dit, au lieu
      // d'afficher une coche verte devant une transcription inchangée.
      if (n === 0) {
        toast.warning(
          lang === "fr"
            ? `Aucun segment renommé — les noms sélectionnés n'ont pas été retrouvés dans les transcriptions (ou ils portent déjà « ${canonical.trim()} »).`
            : `No segment renamed — the selected names were not found in the transcripts (or they already read "${canonical.trim()}").`,
          { duration: 12000 },
        );
        clearChecks();
        if (typeof onSpeakersMerged === "function") await onSpeakersMerged();
        return;
      }
      // Vincent B938 (Eric 2026-02-14 SPEC STRICK PROD) — Undo 60s
      // via `POST /speakers/merge/undo`. La restauration Whisper est
      // exacte (via `original_speaker` snapshot), Fireflies + Merged
      // best-effort (tous les segments `canonical` retournent au 1er
      // nom de la liste — heuristique valide immédiatement après merge).
      const performUndo = async () => {
        try {
          const r = await axios.post(
            `${API}/api/videos/${videoId}/speakers/merge/undo`,
            { canonical: canonical.trim(), names: checkedArr },
            { withCredentials: true },
          );
          const restored = r.data?.total_segments_restored ?? 0;
          // Tâche 1360 — Un segment sans instantané n'est PAS restauré (on ne
          // devine plus son nom d'origine). Le taire laisserait croire que
          // l'annulation a tout remis en place.
          const sansInstantane = r.data?.segments_sans_instantane ?? 0;
          toast.success(
            lang === "fr"
              ? `↩️ Fusion annulée (${restored} segment(s) restaurés)`
              : `↩️ Merge undone (${restored} segments restored)`,
          );
          if (sansInstantane > 0) {
            toast.warning(
              lang === "fr"
                ? `${sansInstantane} segment(s) portaient déjà « ${canonical.trim()} » avant la fusion : ils sont laissés tels quels, leur nom d'origine n'est pas connu.`
                : `${sansInstantane} segment(s) already read "${canonical.trim()}" before the merge: they are left untouched, their original name is unknown.`,
              { duration: 12000 },
            );
          }
          if (typeof onSpeakersMerged === "function") await onSpeakersMerged();
        } catch (uerr) {
          toast.error(
            (lang === "fr" ? "Erreur undo : " : "Undo error: ") +
              (uerr.response?.data?.detail || uerr.message),
          );
        }
      };
      toast.success(
        lang === "fr"
          ? `✅ ${checkedArr.length} locuteurs fusionnés sous « ${canonical.trim()} » (${n} segment(s) renommés)`
          : `✅ ${checkedArr.length} speakers merged as "${canonical.trim()}" (${n} segments renamed)`,
        {
          duration: 60000,
          action: {
            label: lang === "fr" ? "Annuler" : "Undo",
            onClick: performUndo,
          },
        },
      );
      clearChecks();
      if (typeof onSpeakersMerged === "function") await onSpeakersMerged();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur fusion : " : "Merge error: ") +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setMergeSubmitting(false);
    }
  };
  if (allNames.length === 0) return null;

  const sourceLabels = {
    whisper: "Whisper",
    fireflies: "Fireflies",
    merged: lang === "fr" ? "Fusionnée" : "Merged",
  };
  const _isActive = (src) => src === activeSubTab;
  const _hasDataInActive = (name) => {
    const st = speakerStatsBySource[name]?.[activeSubTab];
    return !!(st && (st.turns > 0));
  };

  // Tâche 1399 — Le classement de TOUS les écarts, pour savoir s'il y a
  // quelque chose à montrer et combien de locuteurs dépassent le seuil.
  // Le bouton n'apparaît que si au moins deux sources sont comparables pour
  // un locuteur : proposer une vue d'ensemble vide ferait chercher un
  // problème qui n'existe pas.
  const ecartsClasses = classerEcarts(speakerStatsBySource, sourcesPresentes, seuilDivergencePct);
  const ecartsComparables = ecartsClasses.filter((l) => l.pair).length;
  const ecartsAuDela = ecartsClasses.filter((l) => l.depasse).length;

  // Tâche 1406 (Eric 2026-09-30) — Quand une source porte les vrais noms et
  // l'autre des « Speaker A/B », on peut reporter les noms.
  //
  // Le sens est imposé par ce qu'on sait ÉCRIRE : seule la transcription
  // principale (Whisper) a un point d'entrée de renommage
  // (`/speakers/validate`). Fireflies reste en lecture. C'est aussi le sens
  // réel du besoin : Fireflies connaît les participants de la réunion, Whisper
  // ne rend que des étiquettes. Proposer le bouton dans l'autre sens
  // promettrait une action qui n'existe pas.
  const _segsNommes = (segmentsBySource && segmentsBySource.fireflies) || [];
  const _segsGeneriques = (segmentsBySource && segmentsBySource.whisper) || [];
  const appariementsProposes = (
    _segsNommes.some((x) => !estGenerique(x.speaker))
    && _segsGeneriques.some((x) => estGenerique(x.speaker))
  ) ? proposerAppariements(_segsNommes, _segsGeneriques) : [];

  return (
    <div
      className="border-b border-zinc-100 bg-white"
      data-testid="speaker-legend-box"
      data-collapsed={collapsed ? "true" : "false"}
    >
      {/* Header ─ toujours visible, avec triangle collapse à droite */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-zinc-50 transition-colors"
        data-testid="speaker-legend-box-toggle"
        aria-expanded={!collapsed}
        title={collapsed
          ? (lang === "fr" ? "Développer la liste des locuteurs" : "Expand speakers list")
          : (lang === "fr" ? "Réduire la liste des locuteurs" : "Collapse speakers list")}
      >
        <span className="text-[11px] font-semibold text-zinc-700 flex items-center gap-2">
          {lang === "fr" ? "Locuteurs" : "Speakers"}
          <span
            className="text-[10px] font-normal text-zinc-500 tabular-nums"
            data-testid="speaker-legend-box-count"
          >
            · {allNames.length}
          </span>
        </span>
        {collapsed
          ? <CaretDown size={12} weight="bold" className="text-zinc-500" />
          : <CaretUp size={12} weight="bold" className="text-zinc-500" />}
      </button>

      {!collapsed && (
        <div className="px-3 pb-2" data-testid="speaker-legend-box-body">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="speaker-legend-table">
              <thead>
                <tr className="text-left text-[10px] font-semibold text-zinc-500 border-b border-zinc-200">
                  {canEdit && videoId && (
                    <th className="w-6 px-1 py-1" data-testid="speaker-legend-col-header-check">
                      {/* Vincent B937 — colonne cases à cocher pour fusion */}
                    </th>
                  )}
                  <th className="px-2 py-1">{lang === "fr" ? "Locuteur" : "Speaker"}</th>
                  {["whisper", "fireflies", "merged"].map((src) => {
                    // Vincent B933 (Eric 2026-02-14 SPEC STRICK PROD) —
                    // Toutes les colonnes gardent la même couleur ; SEULE
                    // la colonne de la source active est encadrée (border
                    // gauche/droite + fond très léger + couleur du header
                    // renforcée). Plus lisible que griser 2 colonnes sur 3.
                    const active = _isActive(src);
                    return (
                      <th
                        key={src}
                        className={`px-2 py-1 text-right text-zinc-800 ${
                          active
                            ? "bg-[#002FA7]/5 border-l-2 border-r-2 border-t-2 border-[#002FA7]/60 rounded-t-md"
                            : ""
                        }`}
                        data-testid={`speaker-legend-col-header-${src}`}
                        data-active-col={active ? "true" : "false"}
                      >
                        {sourceLabels[src]}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {allNames.map((name, rowIdx) => {
                  const isFilterActive = speakerFilter === name;
                  const color = speakerColorMap[name] || "#71717a";
                  const inActive = _hasDataInActive(name);
                  const isLastRow = rowIdx === allNames.length - 1;
                  // Vincent B922 (Eric 2026-02-14) — Repère les vrais
                  // désaccords entre deux sources.
                  // Tâche 1397 (Eric 2026-09-29) — Deux corrections : le
                  // badge ne se compare plus à une source qui n'existe pas,
                  // et il mesure le TEMPS DE PAROLE et les MOTS au lieu des
                  // tours. Détail du raisonnement dans `lib/speakerDivergence`.
                  const div = worstDivergence(
                    speakerStatsBySource[name],
                    sourcesPresentes,
                  );
                  const divRatio = div.ratio;
                  const hasBigDivergence = divRatio > (seuilDivergencePct / 100) && inActive;
                  const divPct = Math.round(divRatio * 100);
                  const divLabel = div.pair
                    ? (lang === "fr"
                        ? `${sourceLabels[div.pair[0]]} et ${sourceLabels[div.pair[1]]} ne rapportent pas la même chose : `
                          + `${Math.round(div.seconds * 100)} % d'écart sur le temps de parole, `
                          + `${Math.round(div.words * 100)} % sur le nombre de mots.`
                        : `${sourceLabels[div.pair[0]]} and ${sourceLabels[div.pair[1]]} do not report the same thing: `
                          + `${Math.round(div.seconds * 100)}% gap on speaking time, `
                          + `${Math.round(div.words * 100)}% on word count.`)
                    : "";
                  // Une source qui ne connaît pas ce locuteur n'est pas un
                  // désaccord : c'est une absence. On le dit tel quel.
                  const absentDe = (div.absentes || []).map((k) => sourceLabels[k]);
                  const absentLabel = absentDe.length
                    ? (lang === "fr"
                        ? `Ce nom n'apparaît pas dans : ${absentDe.join(", ")}.`
                        : `This name does not appear in: ${absentDe.join(", ")}.`)
                    : "";
                  return (
                    <tr
                      key={name}
                      className={`border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 ${
                        hasBigDivergence ? "bg-red-50" : ""
                      }`}
                      data-testid={`speaker-legend-row-${name}`}
                      data-in-active-source={inActive ? "true" : "false"}
                      data-divergence-ratio={divRatio.toFixed(2)}
                      data-big-divergence={hasBigDivergence ? "true" : "false"}
                    >
                      {canEdit && videoId && (
                        <td className="w-6 px-1 py-1" data-testid={`speaker-legend-check-${name}`}>
                          {/* Vincent B937 — case à cocher pour la fusion.
                              Sélectionner ≥2 lignes puis clic sur
                              « Fusionner ces N locuteurs » (bouton dans
                              la ligne d'action juste en-dessous du tbody). */}
                          <input
                            type="checkbox"
                            checked={checkedNames.has(name)}
                            onChange={() => toggleCheck(name)}
                            disabled={mergeSubmitting}
                            className="h-3.5 w-3.5 accent-[#002FA7] cursor-pointer"
                            aria-label={lang === "fr"
                              ? `Sélectionner ${name} pour la fusion`
                              : `Select ${name} to merge`}
                            data-testid={`speaker-legend-check-input-${name}`}
                          />
                        </td>
                      )}
                      <td className="px-2 py-1">
                        <button
                          type="button"
                          onClick={() => toggleSpeakerFilter(name)}
                          className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                            isFilterActive
                              ? "border-2 shadow-sm"
                              : "border-transparent hover:border-zinc-200"
                          }`}
                          style={{
                            color,
                            borderColor: isFilterActive ? color : undefined,
                            backgroundColor: isFilterActive ? `${color}1a` : undefined,
                          }}
                          data-testid={`speaker-legend-chip-${name}`}
                          data-active={isFilterActive ? "true" : "false"}
                          aria-pressed={isFilterActive}
                          title={inActive
                            ? (lang === "fr"
                                ? `Filtrer les tours de parole de ${name}`
                                : `Filter turns by ${name}`)
                            : (lang === "fr"
                                ? `${name} est absent de la transcription ${sourceLabels[activeSubTab]}`
                                : `${name} is missing from the ${sourceLabels[activeSubTab]} transcript`)}
                          disabled={!inActive}
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: color }}
                            aria-hidden="true"
                          />
                          {isFilterActive && <span aria-hidden="true">✓</span>}
                          <span>{name}</span>
                        </button>
                        {hasBigDivergence && (
                          // Tâche 1398 — Le badge ouvre les deux sources qui
                          // se contredisent. Un pourcentage seul dit qu'il y
                          // a un problème sans jamais dire lequel.
                          <button
                            type="button"
                            onClick={() => setDivergenceOuverte({ name, pair: div.pair })}
                            className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold text-red-700 px-1 py-0.5 rounded bg-red-100 border border-red-300 hover:bg-red-200 transition-colors cursor-pointer"
                            data-testid={`speaker-legend-divergence-badge-${name}`}
                            title={`${divLabel} ${lang === "fr" ? "Cliquer pour comparer." : "Click to compare."}`}
                          >
                            ⚠ {divPct}%
                          </button>
                        )}
                        {!hasBigDivergence && absentDe.length > 0 && (
                          <span
                            className="ml-1.5 inline-flex items-center text-[9px] font-medium text-zinc-500 px-1 py-0.5 rounded bg-zinc-100 border border-zinc-200"
                            data-testid={`speaker-legend-absent-badge-${name}`}
                            title={absentLabel}
                          >
                            {lang === "fr" ? "absent de " : "not in "}
                            {absentDe.join(", ")}
                          </span>
                        )}
                      </td>
                      {["whisper", "fireflies", "merged"].map((src) => {
                        const st = speakerStatsBySource[name]?.[src];
                        const active = _isActive(src);
                        // Vincent B933 (Eric 2026-02-14 SPEC STRICK PROD) —
                        // Toutes les cellules gardent la même intensité de
                        // couleur ; la colonne active est encadrée par un
                        // trait bleu de gauche à droite + fond très léger.
                        // La dernière ligne ferme le cadre par le bas via
                        // le rendu tbody final.
                        return (
                          <td
                            key={src}
                            className={`px-2 py-1 text-right tabular-nums whitespace-nowrap text-zinc-800 ${
                              active
                                ? `bg-[#002FA7]/5 border-l-2 border-r-2 border-[#002FA7]/60${isLastRow ? " border-b-2 rounded-b-md" : ""}`
                                : ""
                            }`}
                            data-testid={`speaker-legend-cell-${name}-${src}`}
                          >
                            <span
                              className="font-medium"
                              data-testid={`speaker-legend-turns-${name}-${src}`}
                            >
                              {_fmtTurns(st?.turns, lang)}
                            </span>
                            <span className="ml-1 text-zinc-500">
                              · {_fmtMin(st?.seconds, lang)} min
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Tâche 1399 (Eric 2026-09-30) — Un seul bouton pour voir tous les
              désaccords de la vidéo, classés du pire au meilleur. Il est posé
              sous le tableau, là où les badges viennent d'être lus. */}
          {ecartsComparables > 0 && (
            <button
              type="button"
              onClick={() => setEcartsGroupesOuverts(true)}
              className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded border transition-colors ${
                ecartsAuDela > 0
                  ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                  : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid="speaker-legend-voir-tous-ecarts"
              data-au-dela={ecartsAuDela}
              title={lang === "fr"
                ? "Tableau récapitulatif : temps de parole par source et écart, du pire au meilleur."
                : "Summary table: speaking time per source and gap, worst first."}
            >
              {lang === "fr" ? "Voir tous les écarts" : "See all gaps"}
              {ecartsAuDela > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold"
                  data-testid="speaker-legend-voir-tous-ecarts-count"
                >
                  {ecartsAuDela}
                </span>
              )}
            </button>
          )}
          {/* Tâche 1406 — Reporter les noms de Fireflies sur les « Speaker A/B »
              de Whisper. Rien n'est appliqué sans confirmation : une
              attribution fausse qui a l'air vraie contamine les connaissances
              et la recherche par locuteur (acquis 1300). */}
          {appariementsProposes.length > 0 && (
            <button
              type="button"
              onClick={() => setAppariementOuvert(true)}
              className="mt-2 ml-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded border border-[#002FA7]/30 bg-[#002FA7]/5 text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors"
              data-testid="speaker-legend-appariement-auto"
              data-proposes={appariementsProposes.length}
              title={lang === "fr"
                ? "Reporter les noms de Fireflies sur les locuteurs anonymes de Whisper, d'après le temps de parole commun."
                : "Carry the Fireflies names over to Whisper's anonymous speakers, based on shared speaking time."}
            >
              {lang === "fr" ? "Attribuer les locuteurs" : "Assign speakers"}
              <span className="inline-flex items-center justify-center min-w-4 px-1 rounded-full bg-[#002FA7] text-white text-[9px] font-bold">
                {appariementsProposes.length}
              </span>
            </button>
          )}
          {/* Vincent B937 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton
              « Fusionner ces N locuteurs » : visible dès qu'au moins 2
              lignes sont cochées. Utilise `normalizeSpeakerKey` côté
              server pour matcher les variantes NBSP / NFD / casse. */}
          {canEdit && videoId && checkedNames.size >= 2 && (
            <div
              className="mt-2 flex items-center gap-2 text-[11px]"
              data-testid="speaker-legend-merge-bar"
            >
              <span className="text-zinc-600">
                {lang === "fr"
                  ? `${checkedNames.size} locuteurs sélectionnés :`
                  : `${checkedNames.size} speakers selected:`}
              </span>
              <span className="text-zinc-700 font-medium flex-1 truncate" title={Array.from(checkedNames).join(", ")}>
                {Array.from(checkedNames).map((n) => {
                  // Utilise normalizeSpeakerKey pour un affichage stable —
                  // pas critique, juste pour l'exposition data-* du test.
                  const _norm = normalizeSpeakerKey(n);
                  return (
                    <span
                      key={n}
                      className="inline-flex items-center px-1.5 py-0.5 mr-1 rounded bg-[#002FA7]/10 text-[#002FA7]"
                      data-norm-key={_norm}
                    >
                      {n}
                    </span>
                  );
                })}
              </span>
              <button
                type="button"
                onClick={handleMerge}
                disabled={mergeSubmitting}
                className="text-[11px] font-semibold px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="speaker-legend-merge-btn"
                title={lang === "fr"
                  ? "Renomme tous les segments (Whisper + Fireflies + Fusionnée) sur le nom canonique choisi."
                  : "Rename all segments (Whisper + Fireflies + Merged) under the chosen canonical name."}
              >
                {mergeSubmitting
                  ? (lang === "fr" ? "⏳ Fusion..." : "⏳ Merging...")
                  : (lang === "fr" ? `Fusionner ces ${checkedNames.size} locuteurs` : `Merge these ${checkedNames.size} speakers`)}
              </button>
              <button
                type="button"
                onClick={clearChecks}
                disabled={mergeSubmitting}
                className="text-[10px] text-zinc-500 hover:text-zinc-700 underline"
                data-testid="speaker-legend-merge-clear"
              >
                {lang === "fr" ? "annuler" : "clear"}
              </button>
            </div>
          )}
          {speakerFilter && (
            <button
              type="button"
              onClick={() => toggleSpeakerFilter(speakerFilter)}
              className="mt-1 text-[10px] text-zinc-500 hover:text-zinc-800 underline"
              data-testid="speaker-legend-clear-filter"
            >
              {lang === "fr" ? "Effacer le filtre" : "Clear filter"}
            </button>
          )}

          {/* Tâche 1397 — Le seuil se règle ICI, sous la légende qu'il
              pilote : on ajuste en voyant l'effet, pas depuis un écran de
              réglages à l'autre bout de l'application. Réservé aux
              personnes qui peuvent déjà corriger les transcriptions. */}
          {canEdit && onSeuilChange && (
            <div
              className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-2 text-[10px] text-zinc-500"
              data-testid="speaker-legend-threshold-row"
            >
              <span>{lang === "fr" ? "Alerter au-delà de" : "Warn beyond"}</span>
              <input
                type="number"
                min={10}
                max={95}
                value={seuilBrouillon}
                onChange={(e) => setSeuilBrouillon(e.target.value)}
                className="w-14 px-1.5 py-0.5 border border-zinc-300 rounded text-[10px] text-zinc-800"
                data-testid="speaker-legend-threshold-input"
              />
              <span>{lang === "fr" ? "% d'écart" : "% gap"}</span>
              {String(seuilBrouillon) !== String(seuilDivergencePct) && (
                <button
                  type="button"
                  onClick={() => onSeuilChange(Number(seuilBrouillon))}
                  className="font-semibold text-[#002FA7] hover:underline"
                  data-testid="speaker-legend-threshold-save"
                >
                  {lang === "fr" ? "Enregistrer" : "Save"}
                </button>
              )}
              <span className="text-zinc-400">
                {lang === "fr"
                  ? "— vaut pour toute l'organisation"
                  : "— applies to the whole organisation"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tâche 1314 — la saisie du nom canonique, à l'écran (plus de
          `window.prompt`, que le navigateur peut refuser d'ouvrir). */}
      <Dialog
        open={mergeDialog.open}
        onOpenChange={(v) => setMergeDialog((d) => ({ ...d, open: v }))}
      >
        <DialogContent className="sm:max-w-[520px]" data-testid="speaker-merge-dialog">
          <DialogHeader>
            <DialogTitle>
              {lang === "fr"
                ? `Fusionner ces ${mergeDialog.names.length} locuteurs`
                : `Merge these ${mergeDialog.names.length} speakers`}
            </DialogTitle>
            <DialogDescription>
              {lang === "fr"
                ? "Tous les segments de ces locuteurs (Whisper, Fireflies et Fusionnée) prendront le nom ci-dessous. L'opération reste annulable pendant 60 secondes."
                : "Every segment of these speakers (Whisper, Fireflies and Merged) will take the name below. You can undo for 60 seconds."}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 text-[12px] text-zinc-700" data-testid="speaker-merge-dialog-list">
            {mergeDialog.names.map((n) => (
              <li key={n} className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: speakerColorMap[n] || "#71717a" }}
                  aria-hidden="true"
                />
                <span className="break-words min-w-0">{n}</span>
              </li>
            ))}
          </ul>
          <label className="block text-[11px] font-semibold text-zinc-600 mt-1" htmlFor="speaker-merge-canonical">
            {lang === "fr" ? "Nom canonique" : "Canonical name"}
          </label>
          <Input
            id="speaker-merge-canonical"
            value={mergeDialog.value}
            onChange={(e) => setMergeDialog((d) => ({ ...d, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && mergeDialog.value.trim()) {
                runMerge(mergeDialog.names, mergeDialog.value);
              }
            }}
            autoFocus
            data-testid="speaker-merge-canonical-input"
          />
          <DialogFooter>
            <button
              type="button"
              onClick={() => setMergeDialog((d) => ({ ...d, open: false }))}
              className="text-[12px] px-3 py-1.5 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
              data-testid="speaker-merge-dialog-cancel"
            >
              {lang === "fr" ? "Annuler" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={() => runMerge(mergeDialog.names, mergeDialog.value)}
              disabled={!mergeDialog.value.trim() || mergeSubmitting}
              className="text-[12px] font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="speaker-merge-dialog-confirm"
            >
              {lang === "fr" ? "Fusionner" : "Merge"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tâche 1398 — Comparaison côte à côte, ouverte depuis un badge. */}
      <SpeakerDivergenceModal
        open={Boolean(divergenceOuverte)}
        onClose={() => setDivergenceOuverte(null)}
        name={divergenceOuverte?.name}
        pair={divergenceOuverte?.pair}
        sourceLabels={sourceLabels}
        segmentsBySource={segmentsBySource}
        stats={divergenceOuverte ? speakerStatsBySource[divergenceOuverte.name] : null}
        onSeek={onSeek}
      />

      {/* Tâche 1399 — La vue d'ensemble des écarts. La comparaison côte à
          côte s'ouvre PAR DESSUS elle et la refermer ramène au tableau. */}
      <EcartsGroupesModal
        open={ecartsGroupesOuverts}
        onClose={() => setEcartsGroupesOuverts(false)}
        statsParNom={speakerStatsBySource}
        sourcesPresentes={sourcesPresentes}
        seuilPct={seuilDivergencePct}
        sourceLabels={sourceLabels}
        segmentsBySource={segmentsBySource}
        onSeek={onSeek}
      />

      {/* Tâche 1406 — La confirmation avant d'écrire quoi que ce soit. */}
      <AppariementLocuteursModal
        open={appariementOuvert}
        onClose={() => setAppariementOuvert(false)}
        videoId={videoId}
        segmentsNommes={_segsNommes}
        segmentsGeneriques={_segsGeneriques}
        labelSourceNommee={sourceLabels.fireflies}
        labelSourceGenerique={sourceLabels.whisper}
        onApplied={onSpeakersMerged}
      />
    </div>
  );
};

export default SpeakerLegendBox;
